/**
 * TermsView
 * 
 * Terms of Service page for Plot Planet.
 */

import { PolicyView } from './PolicyView'
import { BRANDING } from '../config/branding'

export function TermsView() {
  return (
    <PolicyView title="Terms of Service" lastUpdated="January 20, 2026">
      <h2>1. Acceptance of Terms</h2>
      <p>
        By accessing or using {BRANDING.name} ("the Service"), you agree to be bound by these 
        Terms of Service. If you do not agree to these terms, please do not use the Service.
      </p>
      <p>
        We reserve the right to update these terms at any time. Continued use of the Service 
        after changes constitutes acceptance of the updated terms.
      </p>

      <h2>2. Description of Service</h2>
      <p>
        {BRANDING.name} is a story development platform that provides tools for writers, 
        screenwriters, and creative professionals to build and organize their narrative projects. 
        The Service includes features for:
      </p>
      <ul>
        <li>Character and world-building management</li>
        <li>Timeline and event tracking</li>
        <li>Image and asset organization</li>
        <li>Story synopsis and writing tools</li>
        <li>Storyboarding and visual planning</li>
        <li>Team collaboration features</li>
      </ul>

      <h2>3. User Accounts</h2>
      <p>
        To access certain features of the Service, you must create an account. You are responsible for:
      </p>
      <ul>
        <li>Providing accurate and complete registration information</li>
        <li>Maintaining the security of your account credentials</li>
        <li>All activities that occur under your account</li>
        <li>Notifying us immediately of any unauthorized access</li>
      </ul>
      <p>
        You must be at least 13 years of age to create an account. By creating an account, you 
        represent that you meet this age requirement.
      </p>

      <h2>4. Subscription and Payment</h2>
      <p>
        {BRANDING.name} offers various subscription plans and payment options:
      </p>
      <ul>
        <li><strong>Free Plan:</strong> Limited features at no cost</li>
        <li><strong>Pro Subscription:</strong> Monthly or yearly recurring payment</li>
        <li><strong>Lifetime Access:</strong> One-time payment for permanent access</li>
        <li><strong>Pay-per-Project:</strong> Individual project purchases</li>
      </ul>
      <p>
        Subscription fees are billed in advance on a monthly or yearly basis. All payments are 
        processed securely through Stripe. Prices are subject to change with reasonable notice.
      </p>

      <h2>5. User Content</h2>
      <p>
        You retain ownership of all content you create using the Service ("User Content"). 
        By using the Service, you grant us a limited license to store, display, and process 
        your User Content solely for the purpose of providing the Service.
      </p>
      <p>
        You are responsible for ensuring that your User Content does not violate any laws or 
        infringe on the rights of others.
      </p>

      <h2>6. Prohibited Uses</h2>
      <p>
        You agree not to use the Service to:
      </p>
      <ul>
        <li>Violate any applicable laws or regulations</li>
        <li>Infringe on intellectual property rights of others</li>
        <li>Upload malicious code or attempt to compromise the Service</li>
        <li>Harass, abuse, or harm other users</li>
        <li>Create content that is illegal, harmful, or objectionable</li>
        <li>Resell or redistribute access to the Service without authorization</li>
        <li>Attempt to reverse engineer or extract source code</li>
      </ul>

      <h2>7. Intellectual Property</h2>
      <p>
        The Service, including its design, features, and underlying technology, is owned by 
        {BRANDING.name} and protected by copyright, trademark, and other intellectual property laws. 
        You may not copy, modify, or distribute any part of the Service without our written consent.
      </p>

      <h2>8. Termination</h2>
      <p>
        We may suspend or terminate your account at any time if you violate these Terms or 
        engage in conduct that we determine is harmful to the Service or other users. You may 
        terminate your account at any time through your account settings.
      </p>
      <p>
        Upon termination, your right to use the Service ceases immediately. We may retain 
        certain data as required by law or for legitimate business purposes.
      </p>

      <h2>9. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, {BRANDING.name} shall not be liable for any 
        indirect, incidental, special, consequential, or punitive damages arising from your 
        use of the Service. Our total liability shall not exceed the amount you paid for the 
        Service in the twelve months preceding the claim.
      </p>
      <p>
        The Service is provided "as is" without warranties of any kind, express or implied.
      </p>

      <h2>10. Dispute Resolution</h2>
      <p>
        Any disputes arising from these Terms or your use of the Service shall be resolved 
        through binding arbitration, except where prohibited by law. You agree to waive any 
        right to participate in class action lawsuits.
      </p>

      <h2>11. Changes to the Service</h2>
      <p>
        We reserve the right to modify, suspend, or discontinue any part of the Service at 
        any time. We will provide reasonable notice of significant changes when possible.
      </p>

      <h2>12. Contact Information</h2>
      <div className="policy-contact">
        <p>
          If you have questions about these Terms of Service, please contact us:
        </p>
        <a href="mailto:support@plotplanet.com">support@plotplanet.com</a>
        <a href="mailto:hello@plotplanet.com">hello@plotplanet.com</a>
      </div>
    </PolicyView>
  )
}

export default TermsView
